/**
 * Única fuente de verdad del **nombre del entorno** (y del release) para la
 * telemetría del frontend.
 *
 * Card u7eQ6itN — `contexts.product_event.environment` decía `production` en
 * staging.
 *
 * Por qué existe este módulo
 * --------------------------
 * Había dos lugares derivando el entorno por su cuenta, con variables
 * distintas:
 *
 *   - `sentry.ts` → `VITE_SENTRY_ENVIRONMENT || MODE || 'development'`
 *     alimentaba el campo **nativo** (`Sentry.init({ environment })`).
 *   - `commonProps.ts` → `MODE || 'production'` alimentaba el **context**
 *     (`contexts.product_event.environment`).
 *
 * `import.meta.env.MODE` es el *modo de build de Vite*, **no** el entorno de
 * deploy: el build de staging en Vercel corre `vite build` sin `--mode`, así
 * que `MODE` vale `production` en los tres deploys. El nombre real del entorno
 * viaja en `VITE_SENTRY_ENVIRONMENT` — medido en el bundle vivo de staging:
 * `VITE_SENTRY_ENVIRONMENT:"staging"` junto a `MODE:"production"`.
 *
 * Resultado: el campo nativo decía `staging` y el context decía `production`
 * para el mismo evento, así que cualquier análisis que agrupara por el context
 * contaba staging como producción.
 *
 * Contrato
 * --------
 * - **Nadie más deriva el entorno.** `buildEnv.test.ts` lee el código de
 *   `lib/sentry.ts` y de `lib/analytics/**` y falla si algún otro módulo de
 *   telemetría menciona `VITE_SENTRY_ENVIRONMENT` o `import.meta.env.MODE`.
 *   Un comentario no es un guard; ese test sí.
 * - **Precedencia**: variable explícita de deploy > modo de build > default.
 *   El modo de build es sólo degradación para local/CI, nunca el entorno.
 * - **Invariante**: ningún campo del context puede contradecir su homónimo
 *   nativo. Hoy eso aplica a `environment` y `release`, los dos únicos
 *   duplicados — por eso los dos se resuelven acá.
 * - **Sin imports.** Ni `@sentry/react`: así lo pueden consumir tanto el
 *   módulo de init de Sentry como los helpers de analytics sin arrastrar el
 *   SDK ni crear ciclos.
 *
 * @see docs/changes/2026-10-08_u7eq6itn-environment-staging.md
 * @see docs/architecture/observability.md §4
 */

/** Default cuando no hay ni variable explícita ni modo de build. */
const FALLBACK_ENVIRONMENT = 'development';

/**
 * Normaliza un valor del build: `trim()` y string vacío ⇒ ausente.
 *
 * El `trim()` no es cosmético: las variables de Vercel pueden traer whitespace
 * —`VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE` llega hoy como `"0.05\n"` en el
 * bundle de staging, medido— y un `"staging\n"` como nombre de entorno
 * rompería cualquier filtro de Sentry en silencio.
 */
function normalize(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Lee una clave del build vía thunk, sin reventar si `import.meta.env` no
 * existe (SSR, runners exóticos).
 *
 * El thunk es deliberado: la referencia a `import.meta.env.<KEY>` queda
 * **estática** en el call site, que es la forma que Vite reemplaza en build y
 * la única que ve los stubs del runner. Un acceso dinámico
 * (`env[name]`) se lee `undefined` bajo vitest — fue el bug de la primera
 * versión de este módulo.
 */
function readBuildEnv(read: () => unknown): string | undefined {
  try {
    return normalize(read());
  } catch {
    return undefined;
  }
}

/**
 * Nombre canónico del entorno para la telemetría: `'staging'`,
 * `'production'`, `'development'`…
 *
 * Es el valor que va **a la vez** al campo nativo de Sentry y a
 * `contexts.product_event.environment`. Los dos salen de acá justamente para
 * que no puedan divergir.
 */
export function resolveEnvironment(): string {
  return (
    readBuildEnv(() => import.meta.env.VITE_SENTRY_ENVIRONMENT) ??
    readBuildEnv(() => import.meta.env.MODE) ??
    FALLBACK_ENVIRONMENT
  );
}

/**
 * Release del deploy (Git SHA) o `undefined` si el build no lo inyectó.
 *
 * Igual que `resolveEnvironment()`: el context lo duplica del campo nativo, así
 * que los dos tienen que leer esto y nada más.
 *
 * Precedencia (card jsCaNZfn): variable explícita > SHA que inyecta Vercel.
 *
 *   1. `VITE_GIT_SHA` — override explícito para builds fuera de Vercel (local,
 *      CI, contenedores). Ningún deploy de Vercel la define hoy.
 *   2. `VITE_VERCEL_GIT_COMMIT_SHA` — Vercel expone sus System Environment
 *      Variables al build de Vite con el prefijo `VITE_`. Medido en los bundles
 *      vivos de staging y de producción el 2026-10-11: presente y con el SHA
 *      completo del commit desplegado.
 *
 * Hasta jsCaNZfn sólo se leía la (1), así que el `release` viajaba vacío en
 * los dos entornos: en el bundle, `release` compilaba a `undefined`.
 */
export function resolveRelease(): string | undefined {
  return (
    readBuildEnv(() => import.meta.env.VITE_GIT_SHA) ??
    readBuildEnv(() => import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA)
  );
}
